/** biome-ignore-all lint/complexity/useLiteralKeys: TS noPropertyAccessFromIndexSignature requires bracket notation on Record<string, unknown> */
import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  type CdwGridState,
  type ExportQueryType,
  type ImportValidationReport,
  type RowValidationEntry,
} from '@cdw-ae/common-data';

import type { Observable } from 'rxjs';
import { SnackbarService } from '@cdw-ae/snackbar';
import { ImportService } from './import.service';

export type ImportSource = 'google-drive' | 'local';

export interface ImportDialogData {
  totalItemCount: number;
  selectedCount: number;
  columnCount: number;
  gridState: CdwGridState;
  queryType: ExportQueryType;
  customerId: number;
  /** Firebase UID (selection-context). NOT job tracking. */
  fbUserId: string;
  userEmail: string;
  filePrefix: string;
  onOpenDrivePicker?: () => Promise<{ fileId: string; name: string; mimeType: string } | null>;
  onWaitForJobCompletion?: (jobId: number) => Observable<Record<string, unknown>>;
}

export interface ImportDialogResult {
  importJobId?: number;
  confirmed?: boolean;
  /** Set to true when the user clicks the "Export Device Information" cross-link */
  requestExportTemplate?: boolean;
}

@Component({
  selector: 'lib-cdw-import-dialog',
  templateUrl: './cdw-import-dialog.component.html',
  styleUrls: ['./cdw-import-dialog.component.scss'],
  standalone: false,
})
export class CdwImportDialogComponent implements OnInit {
  step: 'source' | 'tab-select' | 'file-error' | 'validation' | 'results' | 'importing' = 'source';
  selectedSource: ImportSource = 'google-drive';
  isDragging = false;
  selectedFile: File | null = null;
  driveFileSelected = false;
  driveFileId: string | null = null;
  driveFileName: string | null = null;
  driveMimeType: string | null = null;
  driveError = false;
  validationError: string | null = null;
  importJobId: number | null = null;
  sheetTabs: string[] = [];
  selectedTab: string | null = null;
  validationReport: ImportValidationReport | null = null;
  fileErrorMessage: string | null = null;
  expandedCategories = new Set<string>();
  importMode: 'changes-only' | 'all-values' = 'changes-only';
  readonly checksumHelpUrl = 'https://amplifiedlabs.zendesk.com/hc/en-us/articles/51679615175443-Force-Import';
  /**
   * Set when the user clicks Close while step === 'importing'. Checked in the
   * executeImport `next` callback so we can call cancelImport(executeJobId)
   * and skip the toast subscription that would otherwise fire later.
   */
  private cancelRequested = false;

  constructor(
    public dialogRef: MatDialogRef<CdwImportDialogComponent, ImportDialogResult | undefined>,
    @Inject(MAT_DIALOG_DATA) public data: ImportDialogData,
    private readonly importService: ImportService,
    private readonly snackbarService: SnackbarService
  ) {}

  ngOnInit(): void {
    // Nothing to initialize — dialog opens directly to the upload step.
  }

  get hasBlockingErrors(): boolean {
    const cats = this.validationReport?.errorCategories;
    if (cats?.idVerificationFailed) return true;
    const hardErrors = this.totalHardErrorCount;
    if (hardErrors > 0) return true;
    // If permission restrictions cover ALL valid rows, block
    if (cats && cats.permissionRestrictions > 0 && this.validationReport?.validRows === 0) {
      return true;
    }
    return false;
  }

  /** Category-level errors that block the import (missing, duplicate, invalid format) */
  get totalHardErrorCount(): number {
    const cats = this.validationReport?.errorCategories;
    if (!cats) return 0;
    return cats.missingId + cats.duplicateId + cats.invalidIdFormat;
  }

  get totalErrorCount(): number {
    const cats = this.validationReport?.errorCategories;
    if (cats) {
      return cats.missingId + cats.duplicateId + cats.invalidIdFormat + cats.unrecognizedDevice + cats.permissionRestrictions;
    }
    return this.validationReport?.errorRows ?? 0;
  }

  /** Unique rows in errors[] (deduped because a row may have multiple field errors). */
  get fieldErrorRowCount(): number {
    const errors = this.validationReport?.errors;
    if (!errors?.length) return 0;
    return new Set(errors.map((e) => e.rowNumber)).size;
  }

  get hasFileSelected(): boolean {
    return this.selectedSource === 'local'
      ? this.selectedFile !== null
      : this.driveFileSelected;
  }

  get isNoChangesError(): boolean {
    return this.step === 'file-error'
      && this.validationReport !== null
      && this.validationReport.validRows === 0
      && this.validationReport.errorRows === 0;
  }

  private get shouldBypassChecksum(): boolean {
    return this.importMode === 'all-values';
  }

  /** Visual status for validation results — determines icon/color treatment */
  get resultStatus(): 'success' | 'warning' | 'error' {
    const cats = this.validationReport?.errorCategories;
    if (!cats) return 'success';

    if (cats.idVerificationFailed || cats.missingId > 0 || cats.duplicateId > 0 || cats.invalidIdFormat > 0) {
      return 'error';
    }

    if (cats.unrecognizedDevice > 0 || cats.permissionRestrictions > 0) {
      return 'warning';
    }

    if (this.totalErrorCount > 0) {
      return 'error';
    }

    return 'success';
  }

  get visualStep(): number {
    switch (this.step) {
      case 'source': return 1;
      case 'tab-select':
      case 'file-error':
      case 'validation':
      case 'results': return 2;
      case 'importing': return 3;
      default: return 0;
    }
  }

  isStepCompleted(stepNumber: number): boolean {
    return stepNumber < this.visualStep;
  }

  isStepActive(stepNumber: number): boolean {
    return stepNumber === this.visualStep;
  }

  isStepClickable(stepNumber: number): boolean {
    if (!this.isStepCompleted(stepNumber)) return false;
    if (this.step === 'validation' || this.step === 'importing') return false;
    return true;
  }

  onStepClick(stepNumber: number): void {
    if (!this.isStepClickable(stepNumber)) return;
    if (stepNumber === 1) {
      this.onChooseAnotherFile();
    }
  }

  toggleCategory(category: string): void {
    if (this.expandedCategories.has(category)) {
      this.expandedCategories.delete(category);
    } else {
      this.expandedCategories.add(category);
    }
  }

  isCategoryExpanded(category: string): boolean {
    return this.expandedCategories.has(category);
  }

  hasCategoryIds(category: string): boolean {
    return this.getRowEntries(category).length > 0;
  }

  getRowEntries(category: string): RowValidationEntry[] {
    const map = this.validationReport?.deviceIdsByCategory;
    if (!map) return [];
    if (category === 'updated' || category === 'unchanged') {
      const ids = map[category as 'updated' | 'unchanged'] ?? [];
      return ids.map((deviceId) => ({ rowNumber: 0, deviceId }));
    }
    return ((map as Record<string, unknown>)[category] as RowValidationEntry[] | undefined) ?? [];
  }

  categoryHasRowEntries(category: string): boolean {
    return ['duplicateId', 'invalidIdFormat', 'unrecognizedDevice', 'permissionRestricted'].includes(category);
  }

  copyDeviceId(id: string): void {
    navigator.clipboard.writeText(id);
    this.snackbarService.showMessage('Copied to clipboard');
  }

  copyReference(entry: RowValidationEntry): void {
    const text = entry.deviceId ?? String(entry.rowNumber);
    navigator.clipboard.writeText(text);
    this.snackbarService.showMessage('Copied to clipboard');
  }

  copyAllDeviceIds(category: string): void {
    const entries = this.getRowEntries(category);
    let ids = entries.map((e) => e.deviceId ?? String(e.rowNumber));
    if (category === 'duplicateId') {
      ids = [...new Set(ids)];
    }
    if (ids.length === 0) return;
    navigator.clipboard.writeText(ids.join('\n'));
    this.snackbarService.showMessage(`Copied ${ids.length} IDs to clipboard`);
  }

  copyAllFieldErrors(): void {
    const errors = this.validationReport?.errors;
    if (!errors?.length) return;
    const text = errors.map(e => `${e.deviceId}\t${e.reason}`).join('\n');
    navigator.clipboard.writeText(text);
    this.snackbarService.showMessage(`Copied ${errors.length} errors to clipboard`);
  }

  onCancel(): void {
    if (this.step === 'importing' || this.step === 'validation') {
      this.cancelRequested = true;
      // Post-jobId case: cancel the validation/execute job we already know
      // about. Pre-jobId case (initiate/execute still in flight): the
      // in-flight next-callback will see cancelRequested and cancel using
      // the jobId from the response.
      if (this.importJobId !== null) {
        this.cancelJob(this.importJobId);
      }
    }
    this.dialogRef.close(undefined);
  }

  /** "Export Device Information" cross-link — closes dialog and signals device-search to open the export modal */
  onRequestExportTemplate(): void {
    this.dialogRef.close({ requestExportTemplate: true });
  }

  onImport(): void {
    this.validationError = null;
    this.fileErrorMessage = null;

    if (this.selectedSource === 'google-drive' && this.driveFileId) {
      // Validate mime type
      if (this.driveMimeType && !this.isSupportedDriveMimeType(this.driveMimeType)) {
        this.fileErrorMessage = "This file doesn't seem to be in a Google Sheets or CSV format. Please choose another file.";
        this.step = 'file-error';
        return;
      }

      // Check for multiple tabs first
      this.step = 'validation';
      this.importService.getSheetTabs(this.driveFileId).subscribe({
        next: ({ tabs }) => {
          // User cancelled while we were fetching tabs — don't kick off the
          // import (would create a backend job we'd then have to cancel).
          if (this.cancelRequested) return;
          if (tabs.length > 1) {
            this.sheetTabs = tabs;
            this.selectedTab = null;
            this.step = 'tab-select';
          } else {
            this.startGoogleSheetsImport(tabs[0]);
          }
        },
        error: () => {
          if (this.cancelRequested) return;
          this.validationError = 'Failed to read spreadsheet. Please try again.';
          this.step = 'source';
        },
      });
    } else if (this.selectedSource === 'local' && this.selectedFile) {
      this.step = 'validation';
      this.parseAndUploadCsv(this.selectedFile);
    }
  }

  onConfirmTab(): void {
    // biome-ignore lint/complexity/useSimplifiedLogicExpression: reads more clearly as two independent checks
    if (!this.selectedTab || !this.driveFileId) return;
    this.step = 'validation';
    this.startGoogleSheetsImport(this.selectedTab);
  }

  onChooseAnotherFile(): void {
    this.driveFileSelected = false;
    this.driveFileId = null;
    this.driveFileName = null;
    this.driveMimeType = null;
    this.selectedFile = null;
    this.sheetTabs = [];
    this.selectedTab = null;
    this.fileErrorMessage = null;
    this.importMode = 'changes-only';
    this.step = 'source';
  }

  onChangeFile(): void {
    this.onChooseAnotherFile();
    this.selectedSource = 'google-drive';
  }

  onReupload(): void {
    this.onChooseAnotherFile();
    this.selectedSource = 'local';
  }

  onRevalidate(): void {
    this.validationReport = null;
    this.expandedCategories.clear();
    this.importMode = 'changes-only';
    this.onImport();
  }

  onBypassChecksumGate(): void {
    this.validationReport = null;
    this.expandedCategories.clear();
    this.step = 'validation';

    if (this.selectedSource === 'google-drive' && this.driveFileId) {
      this.startGoogleSheetsImport(this.selectedTab ?? undefined, this.shouldBypassChecksum);
    } else if (this.selectedSource === 'local' && this.selectedFile) {
      this.parseAndUploadCsv(this.selectedFile, this.shouldBypassChecksum);
    }
  }

  onSourceChanged(): void {
    this.selectedFile = null;
    this.driveFileSelected = false;
    this.driveFileId = null;
    this.driveFileName = null;
    this.driveMimeType = null;
    this.driveError = false;
    this.validationError = null;
    this.fileErrorMessage = null;
  }

  async onSelectDriveFile(): Promise<void> {
    this.driveError = false;
    try {
      const result = await this.data.onOpenDrivePicker?.();
      if (result) {
        this.driveFileSelected = true;
        this.driveFileId = result.fileId;
        this.driveFileName = result.name;
        this.driveMimeType = result.mimeType;
      }
    } catch {
      this.driveError = true;
      this.driveFileSelected = false;
      this.driveFileId = null;
      this.driveFileName = null;
      this.driveMimeType = null;
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      this.validateAndSetLocalFile(input.files[0]);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    const files = event.dataTransfer?.files;
    if (files?.length) {
      this.validateAndSetLocalFile(files[0]);
    }
  }

  removeFile(): void {
    this.selectedFile = null;
  }

  onConfirmImport(): void {
    if (!this.importJobId) return;
    this.step = 'importing';

    this.importService.executeImport({
      importJobId: this.importJobId,
    }).subscribe({
      next: (response) => {
        if (response.success && response.importJobId) {
          const executeJobId = response.importJobId;

          // User clicked Close during the brief importing window. The dialog
          // is already closed — fire the cancel to saas-jobs-api and skip the
          // toast subscription. The Firestore listener will surface the
          // cancelled status in the jobs notification panel.
          if (this.cancelRequested) {
            this.cancelJob(executeJobId);
            return;
          }

          // Track the execute job. There's a brief window during the dialog's
          // close animation where the Close button is still clickable; if the
          // user clicks it, onCancel reads this.importJobId. Without this
          // assignment it would still hold the (already-finished) validation
          // jobId, and we'd cancel the wrong workflow while the execute job
          // ran to completion silently.
          this.importJobId = executeJobId;

          // Close dialog — user can track in jobs panel
          this.dialogRef.close({
            importJobId: executeJobId,
            confirmed: true,
          });

          // Wait for execution to complete and show toast
          if (this.data.onWaitForJobCompletion) {
            this.data.onWaitForJobCompletion(executeJobId).subscribe({
              next: () => {
                // Belt-and-suspenders: the early `return` above prevents this
                // subscription when cancelRequested is set at subscribe-time.
                // This guard also covers the (currently-impossible) case where
                // cancellation is set after subscribe but before the listener
                // emits, and protects against future refactors that move the
                // upstream check.
                if (this.cancelRequested) return;
                const validRows = this.validationReport?.validRows ?? 0;
                this.snackbarService.showMessage(
                  `Success! ${validRows} devices imported and updated.`,
                  'Close',
                  { duration: 10000, horizontalPosition: 'center' }
                );
              },
              error: () => {
                if (this.cancelRequested) return;
                const validRows = this.validationReport?.validRows ?? 0;
                const errorRows = this.validationReport?.errorRows ?? 0;
                this.snackbarService.showError(
                  `Oops! ${validRows} devices updated. ${errorRows} devices could not be updated.`,
                  'See Error Log',
                  { duration: 15000, horizontalPosition: 'center' }
                );
                // TODO: wire up error log download on snackbar action click
              },
            });
          }
        }
      },
      error: () => {
        if (this.cancelRequested) return;
        this.snackbarService.showError(
          'Failed to start import. Please try again.',
          'Dismiss',
          { duration: 5000, horizontalPosition: 'center' }
        );
        this.step = 'results';
      },
    });
  }

  private waitForValidation(jobId: number): void {
    if (!this.data.onWaitForJobCompletion) {
      this.validationError = 'Job tracking not available.';
      this.step = 'source';
      return;
    }

    this.data.onWaitForJobCompletion(jobId).subscribe({
      next: (additionalJobData) => {
        // User cancelled — don't update state on a closed dialog and don't
        // fire the follow-up GET /imports/:jobId/validation-report request.
        if (this.cancelRequested) return;
        const errorCategories = additionalJobData['errorCategories'] as
          ImportValidationReport['errorCategories'] | undefined;

        this.validationReport = {
          totalRows: (additionalJobData['totalRows'] as number) ?? 0,
          unchangedRows: (additionalJobData['unchangedRows'] as number) ?? 0,
          changedRows: (additionalJobData['changedRows'] as number) ?? 0,
          validRows: (additionalJobData['validRows'] as number) ?? 0,
          errorRows: (additionalJobData['errorRows'] as number) ?? 0,
          errors: [],
          errorCategories,
        };

        if (this.validationReport.validRows === 0 && this.validationReport.errorRows === 0) {
          this.fileErrorMessage = 'No changes found in this file to be made.';
          this.step = 'file-error';
        } else {
          this.step = 'results';
          this.fetchDeviceIdCategories(jobId);
        }
      },
      error: (err: Error & { additionalJobData?: Record<string, unknown> }) => {
        if (this.cancelRequested) return;
        const parseError = err.additionalJobData?.['parseError'] as string | undefined;
        if (parseError) {
          this.fileErrorMessage = parseError;
          this.step = 'file-error';
        } else {
          this.validationError = err.message || 'Validation failed. Please try again.';
          this.step = 'source';
        }
      },
    });
  }

  private fetchDeviceIdCategories(jobId: number): void {
    this.importService.getValidationReport(jobId).subscribe({
      next: (fullReport) => {
        if (this.validationReport) {
          this.validationReport = {
            ...this.validationReport,
            ...(fullReport.deviceIdsByCategory ? { deviceIdsByCategory: fullReport.deviceIdsByCategory } : {}),
            ...(fullReport.errors?.length ? { errors: fullReport.errors } : {}),
          };
        }
      },
      error: () => {
        // Non-critical: dropdown arrows won't appear if fetch fails
      },
    });
  }

  private startGoogleSheetsImport(tabName?: string, bypassChecksumGate?: boolean): void {
    if (!this.driveFileId) return;

    this.importService.initiateImportGoogleSheets({
      assetType: 'device',
      sheetId: this.driveFileId,
      userEmail: this.data.userEmail,
      sheetTabName: tabName,
      fileName: this.driveFileName ?? undefined,
      ...(bypassChecksumGate ? { bypassChecksumGate } : {}),
    }).subscribe({
      next: (response) => {
        if (response.success && response.importJobId) {
          this.importJobId = response.importJobId;
          // Pre-jobId race: user clicked Cancel while initiate was in flight.
          // Cancel the just-created backend job and skip waitForValidation.
          if (this.cancelRequested) {
            this.cancelJob(response.importJobId);
            return;
          }
          this.waitForValidation(response.importJobId);
        }
      },
      error: () => {
        if (this.cancelRequested) return;
        this.validationError = 'Failed to start import validation. Please try again.';
        this.step = 'source';
      },
    });
  }

  private parseAndUploadCsv(file: File, bypassChecksumGate?: boolean): void {
    const reader = new FileReader();
    reader.onload = () => {
      // User cancelled while we were reading the file — don't POST initiate
      // (would create a backend job we'd then have to cancel).
      if (this.cancelRequested) return;
      const csvContent = reader.result as string;
      if (!(csvContent.includes('\n') || csvContent.includes('\r'))) {
        this.validationError = 'CSV file must contain a header row and at least one data row.';
        this.step = 'source';
        return;
      }

      this.importService.initiateImportCsv({
        assetType: 'device',
        csvContent,
        fileName: file.name,
        ...(bypassChecksumGate ? { bypassChecksumGate } : {}),
      }).subscribe({
        next: (response) => {
          if (response.success && response.importJobId) {
            this.importJobId = response.importJobId;
            // Pre-jobId race: user clicked Cancel while initiate was in flight.
            if (this.cancelRequested) {
              this.cancelJob(response.importJobId);
              return;
            }
            this.waitForValidation(response.importJobId);
          }
        },
        error: () => {
          if (this.cancelRequested) return;
          this.validationError = 'Failed to upload CSV for validation. Please try again.';
          this.step = 'source';
        },
      });
    };
    reader.onerror = () => {
      if (this.cancelRequested) return;
      this.validationError = 'Failed to read the CSV file. Please try again.';
      this.step = 'source';
    };
    reader.readAsText(file);
  }

  private validateAndSetLocalFile(file: File): void {
    this.fileErrorMessage = null;

    if (!file.name.endsWith('.csv')) {
      this.selectedFile = file;
      this.fileErrorMessage = "This file doesn't seem to be in a Google Sheets or CSV format. Please choose another file.";
      this.step = 'file-error';
      return;
    }

    if (file.size === 0) {
      this.selectedFile = file;
      this.fileErrorMessage = 'This file appears to be empty. Please add device data or choose another file.';
      this.step = 'file-error';
      return;
    }

    this.selectedFile = file;
  }

  private cancelJob(jobId: number): void {
    this.importService.cancelImport(jobId).subscribe({
      error: () => {
        this.snackbarService.showError(
          'Failed to cancel import. Check the jobs panel.',
          'Dismiss',
          { duration: 5000, horizontalPosition: 'center' }
        );
      },
    });
  }

  private isSupportedDriveMimeType(mimeType: string): boolean {
    const supported = [
      'application/vnd.google-apps.spreadsheet',
      'text/csv',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ];
    return supported.includes(mimeType);
  }
}
